import { ObjectRef } from 'proto/github.com/solo-io/dev-portal/api/dev-portal/v1beta1/common/common_pb';
import { APIProductDetails } from 'proto/web_app_grpc_pb';
import * as React from 'react';
import { useGetAPIProductVersionDetails, useGetCurrentUser } from '../api/hooks';
import { host } from '../api/web-app';
import DefaultBanner from '../assets/defaultImages/GenericBanner.png';
import { ReactComponent as TagIcon } from '../assets/tag-icon.svg';
import { Loading } from './Loading';
import { SwaggerHolder } from './SwaggerUI/SwaggerHolder';
import { ReactComponent as StacksIcon } from 'assets/document.svg';
import { APIType } from 'proto/github.com/solo-io/dev-portal/api/dev-portal/v1beta1/apiproduct_pb';

const stringToContent = (spec: string | undefined): object | null => {
  if (spec === undefined) {
    return null;
  }
  try {
    return JSON.parse(spec);
  } catch {
    return null;
  }
};

export const APIDetailsCard = ({
  apiProduct,
  environmentRef,
  version
}: {
  apiProduct: APIProductDetails.AsObject;
  environmentRef: ObjectRef.AsObject;
  version: string;
}) => {
  const { currentUser, currentUserError } = useGetCurrentUser();
  const { apiVersionDetails, apiVersionDetailsError, apiVersionDetailsLoading } = useGetAPIProductVersionDetails({
    apiProductRef: {
      name: apiProduct?.metadata?.name! ?? '',
      namespace: apiProduct?.metadata?.namespace! ?? ''
    },
    environmentRef,
    version,
    currentUser: currentUser ?? ''
  });

  // Fall back to the default image if we cannot find the selected image for any reason.
  let [cardImage, setCardImage] = React.useState<JSX.Element>(
    <img
      src={`${host}${apiProduct.imageUrl}`}
      alt=''
      className='w-full h-40 rounded-l-lg'
      onError={() => setCardImage(<img src={DefaultBanner} alt='' className='w-full h-40 rounded-l-lg' />)}
    />
  );

  if (apiVersionDetailsLoading || !stringToContent(apiVersionDetails?.operationsSpec)) {
    return <Loading />;
  }

  const download = () => {
    if (apiVersionDetails?.apiType === APIType.OPEN_API) {
      const templElement = document.createElement('a');
      const file = new Blob([apiVersionDetails?.operationsSpec ?? 'No content for this openApi spec.'], {
        type: 'text/plain'
      });
      templElement.href = URL.createObjectURL(file);
      templElement.download =
        apiProduct.metadata?.namespace + '_' + apiProduct.metadata?.name + '_' + apiVersionDetails?.name + '.json';
      document.body.appendChild(templElement); // Required for this to work in FireFox
      templElement.click();
    }
  };

  return (
    <>
      <div className='flex items-center bg-white border border-gray-400 rounded-lg api-details-card'>
        <div className='flex-shrink-0 w-1/5 mr-2 rounded-lg api-details-api-image-holder'>{cardImage}</div>

        <div className='flex justify-between w-full'>
          <div className='mr-2 space-y-2 api-details-product-properties'>
            <div className='flex items-center'>
              <span className='text-2xl font-medium text-gray-900 api-details-product-name'>
                {apiProduct.displayname}
              </span>
              <span className='px-2 mt-1 ml-2 text-sm text-gray-800 bg-white border border-gray-400 rounded-full api-details-version-name'>
                {version}
              </span>
            </div>
            <div className='flex-grow text-base leading-tight'>{apiProduct.description}</div>
            <div>
              <div className='flex items-center api-details-tags-container'>
                {!!apiVersionDetails?.tagsList?.length && <TagIcon className='w-5 h-5 mr-1 fill-current ' />}
                <span className='flex flex-wrap items-center flex-grow-0 space-x-1 overflow-hidden'>
                  {!!apiVersionDetails?.tagsList?.length &&
                    apiVersionDetails?.tagsList.sort().map(tag => (
                      <span
                        className='px-2 mt-1 text-sm text-gray-800 bg-white border border-gray-400 rounded-full api-card-searchable-name'
                        key={tag}>
                        {tag}
                      </span>
                    ))}
                </span>
              </div>
            </div>
          </div>
          {apiVersionDetails?.apiType === APIType.OPEN_API && (
            <div style={{ color: '#2396C9' }} className={'cursor-pointer mr-4 flex items-center'} onClick={download}>
              {' '}
              <StacksIcon className={'mr-2'} />
              Download OpenAPI Spec
            </div>
          )}
        </div>
      </div>
      <div>
        <SwaggerHolder
          apiProduct={apiProduct}
          environmentRef={environmentRef}
          versionDetails={apiVersionDetails}
          swaggerJSON={stringToContent(apiVersionDetails?.operationsSpec)!}
          disableTryItOut={!!apiVersionDetails?.operationsDisabled}
          portalUrlPrefix={apiVersionDetails?.portalUrlPrefix || ''}
        />
      </div>
    </>
  );
};
